import { useCallback, useEffect, useRef, useState } from "react";
import "../../css/Customers.css";
import {
    fetchCustomers,
    fetchCustomerDetails,
    saveCustomerDetails,
    fetchCustomerMessagingTemplates,
    sendCustomerPushNotification,
    sendCustomerWhatsappMessage,
} from "../../service/api/adminApi";


const filterOptions = [
    { value: "all", label: "All Customers" },
    { value: "ordered_last_7_days", label: "Ordered Last 7 Days" },
    { value: "ordered_last_30_days", label: "Ordered Last 30 Days" },
    { value: "ordered_custom_range", label: "Custom Order Range" },
    { value: "no_orders_30_days", label: "No Orders 30 Days" },
    { value: "no_orders_90_days", label: "No Orders 90 Days" },
    { value: "no_orders_180_days", label: "No Orders 180 Days" },
    { value: "no_orders_1_year", label: "No Orders 1 Year" },
    { value: "never_ordered", label: "Never Ordered" },
    { value: "wallet_balance", label: "Wallet Balance > 0" },
    { value: "wallet_no_orders_30_days", label: "Wallet + No Orders 30 Days" },
    { value: "wallet_no_orders_1_year", label: "Wallet + No Orders 1 Year" },
    { value: "wallet_never_ordered", label: "Wallet + Never Ordered" },
    { value: "new_customers_7_days", label: "New Customers 7 Days" },
    { value: "new_customers_30_days", label: "New Customers 30 Days" },
];

const PER_PAGE = 20;
const TABLE_COLUMNS = 15;

const pad = (value) => String(value).padStart(2, "0");
const toDateInputValue = (date) =>
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const formatDate = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

function CustomerEditModal({ customer, onClose, onSave }) {
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        name: customer?.name || "",
        email: customer?.email || "",
        phone: customer?.phone || "",
        password: "",
        address_id: customer?.addresses?.[0]?.id || "",
        address: customer?.addresses?.[0]?.address || "",
        house: customer?.addresses?.[0]?.house || "",
        tag: customer?.addresses?.[0]?.tag || "",
        landmark: customer?.addresses?.[0]?.landmark || "",
    });

    const set = (field) => (e) => {
        setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await onSave(form);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="customers-modal-overlay">
            <div className="customers-modal">
                <div className="customers-modal-head">
                    <h3>Edit Customer</h3>
                    <button type="button" onClick={onClose}>x</button>
                </div>

                <form onSubmit={submit}>
                    <div className="customers-modal-grid">
                        <div className="customers-modal-field">
                            <label>Name</label>
                            <input value={form.name} onChange={set("name")} required />
                        </div>

                        <div className="customers-modal-field">
                            <label>Email</label>
                            <input value={form.email} onChange={set("email")} required />
                        </div>

                        <div className="customers-modal-field">
                            <label>Phone</label>
                            <input value={form.phone} onChange={set("phone")} required />
                        </div>

                        <div className="customers-modal-field">
                            <label>Password</label>
                            <input
                                type="password"
                                value={form.password}
                                onChange={set("password")}
                                placeholder="Leave empty to keep old password"
                            />
                        </div>
                    </div>

                    <div className="customers-modal-address">
                        <h4>Address</h4>

                        <div className="customers-modal-field">
                            <label>Address</label>
                            <input value={form.address} onChange={set("address")} />
                        </div>

                        <div className="customers-modal-grid">
                            <div className="customers-modal-field">
                                <label>House</label>
                                <input value={form.house} onChange={set("house")} />
                            </div>

                            <div className="customers-modal-field">
                                <label>Tag</label>
                                <input value={form.tag} onChange={set("tag")} />
                            </div>
                        </div>

                        <div className="customers-modal-field">
                            <label>Landmark</label>
                            <input value={form.landmark} onChange={set("landmark")} />
                        </div>
                    </div>

                    <p className="customers-modal-ip">
                        User IP: {customer?.user_ip || "IP Not found"}
                    </p>

                    <div className="customers-modal-actions">
                        <button type="button" className="customers-reset-btn" onClick={onClose}>
                            Cancel
                        </button>

                        <button type="submit" className="customers-secondary-btn" disabled={saving}>
                            {saving ? "Saving..." : "Save Changes"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

function CustomerTargetSummary({ targetType, filterLabel, search, customerCount }) {
    return (
        <div className="customers-target-summary">
            <div className="customers-target-row">
                <span>Target Type</span>
                <strong>{targetType}</strong>
            </div>
            <div className="customers-target-row">
                <span>Filter</span>
                <strong>{filterLabel}</strong>
            </div>
            <div className="customers-target-row">
                <span>Search</span>
                <strong>{search || "-"}</strong>
            </div>
            <div className="customers-target-row">
                <span>Customers</span>
                <strong>{customerCount}</strong>
            </div>
        </div>
    );
}

function CustomerPushDrawer({
                                open,
                                onClose,
                                onSend,
                                form,
                                setForm,
                                targetType,
                                filterLabel,
                                search,
                                customerCount,
                            }) {
    const [sending, setSending] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (open) {
            setForm({ title: "", message: "", deep_link: "" });
            setError("");
        }
    }, [open, setForm]);

    if (!open) return null;

    const submit = async (event) => {
        event.preventDefault();
        if (!form.title.trim() || !form.message.trim()) {
            setError("Notification title and message are required.");
            return;
        }
        if (customerCount <= 0) {
            setError("Select at least one customer.");
            return;
        }

        setSending(true);
        setError("");
        try {
            await onSend({
                title: form.title.trim(),
                message: form.message.trim(),
                deep_link: form.deep_link.trim(),
            });
        } catch (requestError) {
            setError(
                requestError?.response?.data?.message ||
                requestError?.message ||
                "Failed to send push notification."
            );
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="customers-drawer-overlay" role="presentation" onMouseDown={onClose}>
            <aside
                className="customers-drawer"
                role="dialog"
                aria-modal="true"
                aria-labelledby="customers-push-drawer-title"
                onMouseDown={(event) => event.stopPropagation()}
            >
                <div className="customers-drawer-head">
                    <div>
                        <div className="customers-modal-kicker">Bulk Messaging</div>
                        <h3 className="customers-drawer-title" id="customers-push-drawer-title">
                            Send Push Notification
                        </h3>
                        <p className="customers-drawer-subtitle">
                            Compose a notification for the selected customer audience.
                        </p>
                    </div>
                    <button
                        type="button"
                        className="customers-drawer-close"
                        onClick={onClose}
                        disabled={sending}
                        aria-label="Close push notification drawer"
                    >
                        ×
                    </button>
                </div>

                <form className="customers-drawer-form" onSubmit={submit}>
                    <div className="customers-drawer-body">
                        <CustomerTargetSummary
                            targetType={targetType}
                            filterLabel={filterLabel}
                            search={search}
                            customerCount={customerCount}
                        />

                        <div className="customers-message-field">
                            <label htmlFor="customers-push-title">Notification Title</label>
                            <input
                                id="customers-push-title"
                                value={form.title}
                                onChange={(event) =>
                                    setForm((previous) => ({ ...previous, title: event.target.value }))
                                }
                                placeholder="Enter notification title"
                                disabled={sending}
                                required
                            />
                        </div>

                        <div className="customers-message-field">
                            <label htmlFor="customers-push-message">Notification Message</label>
                            <textarea
                                id="customers-push-message"
                                value={form.message}
                                onChange={(event) =>
                                    setForm((previous) => ({ ...previous, message: event.target.value }))
                                }
                                placeholder="Write your notification message"
                                rows={6}
                                disabled={sending}
                                required
                            />
                        </div>

                        <div className="customers-message-field">
                            <label htmlFor="customers-push-link">Optional Deep Link</label>
                            <input
                                id="customers-push-link"
                                value={form.deep_link}
                                onChange={(event) =>
                                    setForm((previous) => ({ ...previous, deep_link: event.target.value }))
                                }
                                placeholder="e.g. /orders or https://..."
                                disabled={sending}
                            />
                        </div>

                        <div className="customers-preview-card">
                            <span>Push Preview</span>
                            <div className="customers-push-preview-content">
                                <div className="customers-push-preview-icon">P</div>
                                <div>
                                    <strong>{form.title.trim() || "Notification title"}</strong>
                                    <p>{form.message.trim() || "Your notification message will appear here."}</p>
                                    {form.deep_link.trim() && <small>{form.deep_link.trim()}</small>}
                                </div>
                            </div>
                        </div>

                        {error && <div className="customers-drawer-error">{error}</div>}
                    </div>

                    <div className="customers-drawer-footer">
                        <button
                            type="button"
                            className="customers-bulk-btn clear"
                            onClick={onClose}
                            disabled={sending}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="customers-bulk-btn push"
                            disabled={sending || customerCount <= 0}
                        >
                            {sending ? "Sending..." : "Send Push Notification"}
                        </button>
                    </div>
                </form>
            </aside>
        </div>
    );
}

const normalizeTemplates = (response) => {
    if (Array.isArray(response)) return response;
    if (Array.isArray(response?.templates)) return response.templates;
    if (Array.isArray(response?.data)) return response.data;
    if (Array.isArray(response?.data?.templates)) return response.data.templates;
    return [];
};

const normalizePlaceholderList = (placeholders) => {
    if (Array.isArray(placeholders)) {
        return placeholders.map((placeholder, index) => {
            if (typeof placeholder === "string" || typeof placeholder === "number") {
                return String(placeholder);
            }
            return String(
                placeholder?.key ||
                placeholder?.name ||
                placeholder?.variable ||
                placeholder?.placeholder ||
                index + 1
            );
        });
    }
    if (placeholders && typeof placeholders === "object") {
        return Object.keys(placeholders);
    }
    return [];
};

const getTemplatePlaceholders = (template) => {
    const suppliedPlaceholders = normalizePlaceholderList(template?.placeholders);
    if (suppliedPlaceholders.length > 0) return suppliedPlaceholders;
    const body = template?.body || template?.content || template?.message || "";
    const matches = [...String(body).matchAll(/\{\s*([^}]+?)\s*\}/g)];
    return [...new Set(matches.map((match) => match[1].trim()))];
};

function CustomerWhatsappDrawer({
                                    open,
                                    onClose,
                                    onSend,
                                    selectedTemplate,
                                    setSelectedTemplate,
                                    targetType,
                                    filterLabel,
                                    search,
                                    customerCount,
                                }) {
    const [templates, setTemplates] = useState([]);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [sending, setSending] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!open) return undefined;

        let active = true;
        setTemplates([]);
        setSelectedTemplate("");
        setError("");
        setLoadingTemplates(true);

        fetchCustomerMessagingTemplates()
            .then((response) => {
                if (active) setTemplates(normalizeTemplates(response));
            })
            .catch((requestError) => {
                if (active) {
                    setError(
                        requestError?.response?.data?.message ||
                        "Failed to load WhatsApp templates."
                    );
                }
            })
            .finally(() => {
                if (active) setLoadingTemplates(false);
            });

        return () => {
            active = false;
        };
    }, [open, setSelectedTemplate]);

    if (!open) return null;

    const template = templates.find(
        (item) => String(item.id ?? item.template_id ?? item.name) === String(selectedTemplate)
    );
    const templatePlaceholders = getTemplatePlaceholders(template);
    const autoPlaceholders = normalizePlaceholderList(template?.auto_placeholders);
    const manualPlaceholders = normalizePlaceholderList(template?.manual_placeholders);
    const hasUnsupportedPlaceholders = manualPlaceholders.length > 0;
    const isBulkSafe = template?.is_bulk_safe !== false;
    const manualPlaceholderWarning =
        "This template contains unsupported/manual variables and cannot be sent.";
    const templatePreview =
        template?.body || template?.content || template?.message || "Select a template to preview it.";

    const handleTemplateChange = (event) => {
        const value = event.target.value;
        setSelectedTemplate(value);
        setError("");
    };

    const submit = async (event) => {
        event.preventDefault();
        if (!selectedTemplate) {
            setError("Please select a WhatsApp template.");
            return;
        }
        if (customerCount <= 0) {
            setError("Select at least one customer.");
            return;
        }
        if (!isBulkSafe || hasUnsupportedPlaceholders) {
            setError(
                hasUnsupportedPlaceholders
                    ? manualPlaceholderWarning
                    : template?.warning || "This template is not valid for customer bulk messaging."
            );
            return;
        }

        setSending(true);
        setError("");
        try {
            await onSend({
                template_id: template?.id ?? template?.template_id ?? selectedTemplate,
            });
        } catch (requestError) {
            setError(
                requestError?.response?.data?.message ||
                requestError?.message ||
                "Failed to send WhatsApp messages."
            );
        } finally {
            setSending(false);
        }
    };

    return (
        <div className="customers-drawer-overlay" role="presentation" onMouseDown={onClose}>
            <aside
                className="customers-drawer"
                role="dialog"
                aria-modal="true"
                aria-labelledby="customers-whatsapp-drawer-title"
                onMouseDown={(event) => event.stopPropagation()}
            >
                <div className="customers-drawer-head">
                    <div>
                        <div className="customers-modal-kicker">Bulk Messaging</div>
                        <h3 className="customers-drawer-title" id="customers-whatsapp-drawer-title">
                            Send WhatsApp Message
                        </h3>
                        <p className="customers-drawer-subtitle">
                            Choose a customer template. Personal details are filled automatically.
                        </p>
                    </div>
                    <button
                        type="button"
                        className="customers-drawer-close"
                        onClick={onClose}
                        disabled={sending}
                        aria-label="Close WhatsApp drawer"
                    >
                        ×
                    </button>
                </div>

                <form className="customers-drawer-form" onSubmit={submit}>
                    <div className="customers-drawer-body">
                        <CustomerTargetSummary
                            targetType={targetType}
                            filterLabel={filterLabel}
                            search={search}
                            customerCount={customerCount}
                        />

                        <div className="customers-message-field">
                            <label htmlFor="customers-whatsapp-template">Template</label>
                            <select
                                id="customers-whatsapp-template"
                                value={selectedTemplate}
                                onChange={handleTemplateChange}
                                disabled={loadingTemplates || sending}
                                required
                            >
                                <option value="">
                                    {loadingTemplates ? "Loading templates..." : "Select a template"}
                                </option>
                                {templates.map((item) => {
                                    const value = item.id ?? item.template_id ?? item.name;
                                    const itemName =
                                        item.name || item.title || item.template_name || `Template ${value}`;
                                    const itemIsBulkSafe = item.is_bulk_safe !== false;
                                    return (
                                        <option
                                            key={value}
                                            value={value}
                                            disabled={!itemIsBulkSafe}
                                        >
                                            {itemIsBulkSafe
                                                ? itemName
                                                : `${itemName} - Not valid for customer bulk`}
                                        </option>
                                    );
                                })}
                            </select>
                        </div>

                        {!loadingTemplates && templates.length === 0 && !error && (
                            <div className="customers-drawer-empty">No WhatsApp templates available.</div>
                        )}

                        <div className="customers-template-preview">
                            <span>Template Preview</span>
                            <p>{templatePreview}</p>
                        </div>

                        {autoPlaceholders.length > 0 && (
                            <div className="customers-auto-placeholders">
                                <span className="customers-auto-placeholders-title">
                                    Auto-filled for each customer
                                </span>
                                <div className="customers-placeholder-badges">
                                    {autoPlaceholders.map((placeholder) => (
                                        <span className="customers-placeholder-badge" key={placeholder}>
                                            {placeholder}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        {template && templatePlaceholders.length === 0 && (
                            <div className="customers-auto-placeholders customers-auto-placeholders-empty">
                                This template does not require customer variables.
                            </div>
                        )}

                        {template && (!isBulkSafe || hasUnsupportedPlaceholders) && (
                            <div className="customers-drawer-error">
                                {hasUnsupportedPlaceholders
                                    ? manualPlaceholderWarning
                                    : template?.warning ||
                                    "This template is not valid for customer bulk messaging."}
                                {hasUnsupportedPlaceholders && (
                                    <div className="customers-placeholder-badges">
                                        {manualPlaceholders.map((placeholder) => (
                                            <span
                                                className="customers-placeholder-badge unsupported"
                                                key={placeholder}
                                            >
                                                {placeholder}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {error && <div className="customers-drawer-error">{error}</div>}
                    </div>

                    <div className="customers-drawer-footer">
                        <button
                            type="button"
                            className="customers-bulk-btn clear"
                            onClick={onClose}
                            disabled={sending}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="customers-bulk-btn whatsapp"
                            disabled={
                                sending ||
                                loadingTemplates ||
                                customerCount <= 0 ||
                                !selectedTemplate ||
                                !isBulkSafe ||
                                hasUnsupportedPlaceholders
                            }
                        >
                            {sending ? "Sending..." : "Send WhatsApp"}
                        </button>
                    </div>
                </form>
            </aside>
        </div>
    );
}

export default function Customers() {
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [guestLoginUrl, setGuestLoginUrl] = useState("");
    const [editingCustomer, setEditingCustomer] = useState(null);
    const [editLoading, setEditLoading] = useState(false);
    const searchTimer = useRef(null);
    const [filter, setFilter] = useState("all");
    const [filterLabel, setFilterLabel] = useState("All Customers");
    const [openFilter, setOpenFilter] = useState(false);
    const [showCustom, setShowCustom] = useState(false);
    const [customFrom, setCustomFrom] = useState("");
    const [customTo, setCustomTo] = useState("");
    const [appliedCustomRange, setAppliedCustomRange] = useState(null);
    const filterRef = useRef(null);
    const [selectedCustomers, setSelectedCustomers] = useState([]);
    const [selectAllMatching, setSelectAllMatching] = useState(false);

    const [showPushModal, setShowPushModal] = useState(false);
    const [showWhatsappModal, setShowWhatsappModal] = useState(false);
    const [pushForm, setPushForm] = useState({
        title: "",
        message: "",
        deep_link: "",
    });
    const [selectedTemplate, setSelectedTemplate] = useState("");
    const [toast, setToast] = useState(null);
    const toastTimer = useRef(null);

    const clearBulkSelection = useCallback(() => {
        setSelectedCustomers([]);
        setSelectAllMatching(false);
    }, []);

    const showToast = useCallback((nextToast) => {
        clearTimeout(toastTimer.current);
        setToast(nextToast);
        toastTimer.current = setTimeout(() => setToast(null), 4000);
    }, []);

    useEffect(() => {
        return () => clearTimeout(toastTimer.current);
    }, []);

    const targetCount = selectAllMatching ? total : selectedCustomers.length;
    const targetTypeLabel = selectAllMatching ? "Filter Result" : "Selected Customers";

    const buildTargetPayload = useCallback(() => {
        if (selectAllMatching) {
            return {
                target_type: "filter",
                filter,
                search: debouncedSearch,
                from: appliedCustomRange?.from || null,
                to: appliedCustomRange?.to || null,
            };
        }

        return {
            target_type: "selected",
            user_ids: selectedCustomers,
        };
    }, [appliedCustomRange, debouncedSearch, filter, selectAllMatching, selectedCustomers]);

    const getBackendSummary = (response) => {
        const summary = response?.summary || response?.data?.summary;
        if (typeof summary === "string") return summary;
        if (summary && typeof summary === "object") {
            return Object.entries(summary)
                .map(([key, value]) => `${key.replaceAll("_", " ")}: ${value}`)
                .join(" · ");
        }
        return response?.message || response?.data?.message || "";
    };

    const handleSendPush = async (form) => {
        try {
            const response = await sendCustomerPushNotification({
                ...buildTargetPayload(),
                ...form,
            });
            setShowPushModal(false);
            clearBulkSelection();
            showToast({
                type: "success",
                title: "Push notification sent successfully",
                message: getBackendSummary(response),
            });
        } catch (error) {
            showToast({
                type: "error",
                title: "Push notification failed",
                message: error?.response?.data?.message || "Please try again.",
            });
            throw error;
        }
    };

    const handleSendWhatsapp = async (form) => {
        try {
            const response = await sendCustomerWhatsappMessage({
                ...buildTargetPayload(),
                ...form,
            });
            setShowWhatsappModal(false);
            clearBulkSelection();
            showToast({
                type: "success",
                title: "WhatsApp messages queued/sent successfully",
                message: getBackendSummary(response),
            });
        } catch (error) {
            showToast({
                type: "error",
                title: "WhatsApp message failed",
                message: error?.response?.data?.message || "Please try again.",
            });
            throw error;
        }
    };
    const handleLoginAsCustomer = (url) => {
        if (!url) {
            alert("Login URL not found");
            return;
        }

        const title = "Login as Customer";
        const width = 390;
        const height = 680;
        const left = window.screen.width / 2 - width / 2;
        const top = window.screen.height / 2 - height / 2;

        const features =
            `width=${width},height=${height},top=${top},left=${left},` +
            "directories=no,titlebar=no,toolbar=no,menubar=no,location=no," +
            "resizable=no,scrollbars=no,status=no";

        window.open(url, title, features);
    };

    const loadCustomers = useCallback(async () => {
        try {
            setLoading(true);

            const params = {
                page: currentPage,
                per_page: PER_PAGE,
                search: debouncedSearch,
                filter,
            };

            if (filter === "ordered_custom_range" && appliedCustomRange) {
                params.from = appliedCustomRange.from;
                params.to = appliedCustomRange.to;
            }

            const res = await fetchCustomers(params);

            setGuestLoginUrl(res?.guest_login_url || "");
            setCustomers(res?.data || []);
            setCurrentPage(res?.current_page || 1);
            setLastPage(res?.last_page || 1);
            setTotal(res?.total || 0);
        } catch (error) {
            console.error(error);
            alert(error?.response?.data?.message || "Failed to load customers");
        } finally {
            setLoading(false);
        }
    }, [appliedCustomRange, currentPage, debouncedSearch, filter]);

    const openEditCustomer = async (customerId) => {
        try {
            setEditLoading(true);
            const res = await fetchCustomerDetails(customerId);
            setEditingCustomer(res?.data || null);
        } catch (error) {
            console.error(error);
            alert("Failed to load customer details");
        } finally {
            setEditLoading(false);
        }
    };

    useEffect(() => {
        loadCustomers();
    }, [loadCustomers]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (filterRef.current && !filterRef.current.contains(event.target)) {
                setOpenFilter(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleFilterSelect = (item) => {
        if (item.value === "ordered_custom_range") {
            setShowCustom(true);
            setOpenFilter(false);
            return;
        }

        setFilter(item.value);
        setFilterLabel(item.label);
        setShowCustom(false);
        setCustomFrom("");
        setCustomTo("");
        setAppliedCustomRange(null);
        setCurrentPage(1);
        clearBulkSelection();
    };

    const applyCustomFilter = () => {
        if (!customFrom || !customTo) {
            alert("Please select from and to dates");
            return;
        }

        if (customFrom > customTo) {
            alert("From date cannot be after to date");
            return;
        }

        setFilter("ordered_custom_range");
        setAppliedCustomRange({ from: customFrom, to: customTo });
        setFilterLabel(`${formatDate(customFrom)} - ${formatDate(customTo)}`);
        setOpenFilter(false);
        setShowCustom(false);
        setCurrentPage(1);
        clearBulkSelection();
    };
    const handleSearch = (e) => {
        const value = e.target.value;
        setSearch(value);

        clearTimeout(searchTimer.current);
        searchTimer.current = setTimeout(() => {
            setDebouncedSearch(value);
            setCurrentPage(1);
            clearBulkSelection();
        }, 400);
    };

    const resetFilters = () => {
        clearTimeout(searchTimer.current);
        setSearch("");
        setDebouncedSearch("");
        setFilter("all");
        setFilterLabel("All Customers");
        setShowCustom(false);
        setCustomFrom("");
        setCustomTo("");
        setAppliedCustomRange(null);
        setCurrentPage(1);
        clearBulkSelection();
    };

    const goToPage = (page) => {
        if (page < 1 || page > lastPage) return;
        setCurrentPage(page);
        clearBulkSelection();
    };

    return (
        <div className="customers-page">
            <div className="customers-head">
                <div>
                    <h2>Customers</h2>
                    <p>View and manage customer details</p>
                </div>

                <div className="customers-actions">
                    <button
                        className="customers-secondary-btn"
                        type="button"
                        onClick={() => handleLoginAsCustomer(guestLoginUrl)}
                    >
                        Order for Guest
                    </button>
                    <button
                        className="customers-reset-btn"
                        type="button"
                        onClick={resetFilters}
                    >
                        Reset All Filters
                    </button>
                    <div className="customers-filter-wrap" ref={filterRef}>
                        <button
                            type="button"
                            className="customers-filter-btn"
                            onClick={() => setOpenFilter((prev) => !prev)}
                        >
                            {filterLabel}
                        </button>

                        {openFilter && (
                            <div className="customers-filter-menu">
                                {filterOptions.map((item) => (
                                    <button
                                        key={item.value}
                                        type="button"
                                        className={`customers-filter-item ${filter === item.value ? "active" : ""}`}
                                        onClick={() => handleFilterSelect(item)}
                                    >
                                        {item.label}
                                    </button>
                                ))}
                            </div>
                        )}

                        {showCustom && (
                            <div className="customers-custom-box">
                                <input
                                    type="date"
                                    value={customFrom}
                                    max={toDateInputValue(new Date())}
                                    onChange={(e) => setCustomFrom(e.target.value)}
                                />

                                <input
                                    type="date"
                                    value={customTo}
                                    min={customFrom || undefined}
                                    max={toDateInputValue(new Date())}
                                    onChange={(e) => setCustomTo(e.target.value)}
                                />

                                <button type="button" onClick={applyCustomFilter}>
                                    Apply
                                </button>
                            </div>
                        )}

                    </div>
                </div>
            </div>

            <div className="customers-card">

                <div className="customers-bulk-panel">
                    <div className="customers-bulk-left">
                        <label className="customers-bulk-main">
                            <input
                                type="checkbox"
                                checked={selectAllMatching}
                                onChange={(event) => {
                                    setSelectAllMatching(event.target.checked);
                                    setSelectedCustomers([]);
                                }}
                            />
                            <span>
                                <strong className="customers-bulk-title">Select All Matching</strong>
                                <span className="customers-bulk-meta">
                                    Target all {total} customers in the current result
                                </span>
                            </span>
                        </label>

                        <div className="customers-bulk-meta">
                            <span className="customers-bulk-chip">{targetCount} selected</span>
                            <span className="customers-bulk-chip">{filterLabel}</span>
                            {debouncedSearch && (
                                <span className="customers-bulk-chip">Search: {debouncedSearch}</span>
                            )}
                        </div>
                    </div>

                    <div className="customers-bulk-actions">
                        {/*<button*/}
                        {/*    type="button"*/}
                        {/*    className="customers-bulk-btn push"*/}
                        {/*    disabled={targetCount <= 0}*/}
                        {/*    onClick={() => setShowPushModal(true)}*/}
                        {/*>*/}
                        {/*    Send Push*/}
                        {/*</button>*/}
                        <button
                            type="button"
                            className="customers-bulk-btn whatsapp"
                            disabled={targetCount <= 0}
                            onClick={() => setShowWhatsappModal(true)}
                        >
                            Send WhatsApp
                        </button>
                        <button
                            type="button"
                            className="customers-bulk-btn clear"
                            disabled={targetCount <= 0}
                            onClick={clearBulkSelection}
                        >
                            Clear
                        </button>
                    </div>
                </div>

                {selectAllMatching && (
                    <div className="customers-bulk-warning">
                        Messages will be sent to all customers matching current filters.
                    </div>
                )}

                <div className="customers-toolbar">
                    <input
                        className="customers-search"
                        type="text"
                        value={search}
                        onChange={handleSearch}
                        placeholder="Search with name, email, phone..."
                    />

                    <div className="customers-count">
                        {total} Customers
                    </div>
                </div>

                <div className="customers-table-wrap">
                    <table className="customers-table">
                        <thead>
                        <tr>
                            <th>
                                <input
                                    type="checkbox"
                                    checked={
                                        customers.length > 0 &&
                                        customers.every(c =>
                                            selectedCustomers.includes(c.id)
                                        )
                                    }
                                    onChange={(e) => {
                                        if (e.target.checked) {
                                            setSelectedCustomers(
                                                customers.map(c => c.id)
                                            );
                                        } else {
                                            setSelectedCustomers([]);
                                        }
                                        setSelectAllMatching(false);
                                    }}
                                />
                            </th>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Top Store</th>
                            <th>Role</th>
                            <th>Wallet</th>
                            <th>Total</th>
                            <th>Completed</th>
                            <th>Cancelled</th>
                            <th>Filter Total</th>
                            <th>Last Order</th>
                            <th>Address</th>
                            <th>Version</th>
                            <th>Created Date</th>
                            <th>Action</th>
                        </tr>
                        </thead>

                        <tbody>
                        {loading ? (
                            Array.from({ length: 6 }).map((_, index) => (
                                <tr key={index}>
                                    {Array.from({ length: TABLE_COLUMNS }).map((__, i) => (
                                        <td key={i}>
                                            <div className="customers-skeleton" />
                                        </td>
                                    ))}
                                </tr>
                            ))
                        ) : customers.length > 0 ? (
                            customers.map((customer) => (
                                <tr key={customer.id}>
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={selectedCustomers.includes(customer.id)}
                                            onChange={(e) => {

                                                if (e.target.checked) {
                                                    setSelectedCustomers(prev => [
                                                        ...prev.filter(id => id !== customer.id),
                                                        customer.id
                                                    ]);
                                                } else {
                                                    setSelectedCustomers(prev =>
                                                        prev.filter(id => id !== customer.id)
                                                    );
                                                }
                                                setSelectAllMatching(false);

                                            }}
                                        />
                                    </td>
                                    <td>
                                        <div className="customers-user">
                                            <div className="customers-avatar">
                                                {(customer.name || "C").charAt(0).toUpperCase()}
                                            </div>
                                            <div>
                                                <div className="customers-name">
                                                    {customer.name || "-"}
                                                </div>
                                                <div className="customers-id">
                                                    ID: {customer.id}
                                                </div>
                                            </div>
                                        </div>
                                    </td>

                                    <td>{customer.email || "-"}</td>
                                    <td>{customer.phone || "-"}</td>
                                    <td>
                                        {customer.top_store
                                            ? `${customer.top_store.name} (${customer.top_store.orders})`
                                            : "-"}
                                    </td>
                                    <td>
                                            <span className="customers-role">
                                                {customer.role || "Customer"}
                                            </span>
                                    </td>
                                    <td>Rs. {Number(customer.wallet || 0).toFixed(2)}</td>
                                    <td>{customer.total_orders || 0}</td>
                                    <td>{customer.completed_orders || 0}</td>
                                    <td>{customer.cancelled_orders || 0}</td>
                                    <td>{customer.filter_orders || 0}</td>
                                    <td>{customer.last_order_at ? formatDate(customer.last_order_at) : "-"}</td>
                                    <td className="customers-address">
                                        {customer.address || "-"}
                                    </td>
                                    <td>{customer.app_version || "_"}</td>
                                    <td>{customer.created_at || "-"}</td>
                                    <td>
                                        <div className="customers-action-group">
                                            {customer.actions?.login_as_customer_url && (
                                                <button
                                                    type="button"
                                                    className="customers-action-btn primary"
                                                    onClick={() =>
                                                        handleLoginAsCustomer(customer.actions.login_as_customer_url)
                                                    }
                                                >
                                                    Login
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                className="customers-action-btn"
                                                disabled={editLoading}
                                                onClick={() => openEditCustomer(customer.id)}
                                            >
                                                Edit
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={TABLE_COLUMNS} className="customers-empty">
                                    No customers found
                                </td>
                            </tr>
                        )}
                        </tbody>
                    </table>
                </div>

                <div className="customers-pagination">
                    <button
                        type="button"
                        disabled={currentPage <= 1}
                        onClick={() => goToPage(currentPage - 1)}
                    >
                        Previous
                    </button>

                    <span>
                        Page {currentPage} of {lastPage}
                    </span>

                    <button
                        type="button"
                        disabled={currentPage >= lastPage}
                        onClick={() => goToPage(currentPage + 1)}
                    >
                        Next
                    </button>
                </div>
            </div>

            {editingCustomer && (
                <CustomerEditModal
                    customer={editingCustomer}
                    onClose={() => setEditingCustomer(null)}
                    onSave={async (data) => {
                        await saveCustomerDetails(editingCustomer.id, data);
                        setEditingCustomer(null);
                        loadCustomers();
                    }}
                />
            )}

            <CustomerPushDrawer
                open={showPushModal}
                onClose={() => setShowPushModal(false)}
                onSend={handleSendPush}
                form={pushForm}
                setForm={setPushForm}
                targetType={targetTypeLabel}
                filterLabel={filterLabel}
                search={debouncedSearch}
                customerCount={targetCount}
            />

            <CustomerWhatsappDrawer
                open={showWhatsappModal}
                onClose={() => setShowWhatsappModal(false)}
                onSend={handleSendWhatsapp}
                selectedTemplate={selectedTemplate}
                setSelectedTemplate={setSelectedTemplate}
                targetType={targetTypeLabel}
                filterLabel={filterLabel}
                search={debouncedSearch}
                customerCount={targetCount}
            />

            {toast && (
                <div className={`customers-toast ${toast.type}`} role="status">
                    <strong>{toast.title}</strong>
                    {toast.message && <span>{toast.message}</span>}
                    <button
                        type="button"
                        onClick={() => {
                            clearTimeout(toastTimer.current);
                            setToast(null);
                        }}
                        aria-label="Dismiss notification"
                    >
                        ×
                    </button>
                </div>
            )}
        </div>
    );
}